import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { projectsApi } from '../api/projects';
import { storiesApi } from '../api/stories';
import { documentImportApi } from '../api/documentImport';
import { applyImportNext } from '../components/DocumentImport/importNext';
import { UPLOAD_A_DOC } from '../utils/writerCopy';
import SeriesNotes from '../components/Series/SeriesNotes';
import BookToolsGroup from '../components/BookTools/BookToolsGroup';
import './SeriesBookBoardPage.css';

/**
 * Book Planning Docs room (#823). Same Document Import v1 pile as
 * Series Docs (#672). Not a mode-switch door onto the series Timeline.
 */
export default function BookNotesPage() {
  const { projectId, storyId } = useParams();
  const navigate = useNavigate();
  const {
    currentProject,
    currentStory,
    setCurrentProject,
    setCurrentStory,
    setProjectAndStory,
  } = useApp();

  const [project, setProject] = useState(
    currentProject?.id === projectId ? currentProject : null,
  );
  const [story, setStory] = useState(
    currentStory?.id === storyId ? currentStory : null,
  );
  const [importBatches, setImportBatches] = useState([]);
  const [importsLoading, setImportsLoading] = useState(true);
  const [importsError, setImportsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!projectId || !storyId) return;
      try {
        const nextProject = currentProject?.id === projectId
          ? currentProject
          : await projectsApi.get(projectId);
        const nextStory = currentStory?.id === storyId
          ? currentStory
          : await storiesApi.get(storyId);
        if (cancelled) return;
        if (nextProject) setProject(nextProject);
        if (nextStory) setStory(nextStory);
        if (nextProject && nextStory && setProjectAndStory) {
          setProjectAndStory(nextProject, nextStory);
        } else {
          if (nextProject) setCurrentProject(nextProject);
          if (nextStory) setCurrentStory(nextStory);
        }
      } catch {
        if (!cancelled) {
          setProject(null);
          setStory(null);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [
    projectId,
    storyId,
    currentProject,
    currentStory,
    setCurrentProject,
    setCurrentStory,
    setProjectAndStory,
  ]);

  const loadImports = useCallback(async () => {
    if (!projectId) return;
    setImportsLoading(true);
    setImportsError(null);
    try {
      const data = await documentImportApi.list(projectId);
      setImportBatches(data?.batches || []);
    } catch (err) {
        setImportsError(err.message || 'Could not load docs.');
      setImportBatches([]);
    } finally {
      setImportsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadImports();
  }, [loadImports]);

  const bookName = story?.name || currentStory?.name || 'Docs';

  return (
    <div className="sbb-page">
      <header className="sbb-page__intro">
        <p className="sbb-kicker">Book Planning</p>
        <div className="sbb-title-row">
          <h1 className="sbb-page__title">{bookName}</h1>
          <div className="sbb-title-row__actions">
            <BookToolsGroup />
          </div>
        </div>
      </header>

      <SeriesNotes
        project={project || { id: projectId }}
        storyId={storyId}
        batches={importBatches}
        loading={importsLoading}
        error={importsError}
        onRefresh={loadImports}
        uploadLabel={UPLOAD_A_DOC}
        onImportNext={(payload) => applyImportNext(navigate, payload, project, {
          currentStoryId: storyId,
        })}
      />
    </div>
  );
}
